import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { trackEvent } from '../lib/analytics';
import { findModule } from './curriculum';
import { MODULE1_LESSON_BODIES, MODULE1_KNOWLEDGE_CHECK } from './lessonContent';

export function LessonViewer() {
  const { moduleId, lessonN } = useParams<{ moduleId: string; lessonN: string }>();
  const navigate = useNavigate();
  const [answered, setAnswered] = useState<number | null>(null);
  const [checkAttempted, setCheckAttempted] = useState(false);

  const mod = moduleId ? findModule(moduleId) : undefined;
  const n = Number(lessonN);
  const lesson = mod?.lessons[n - 1];

  if (!mod || !lesson) {
    return (
      <div className="tr-root">
        <div className="tr-wrap tr-appsec">
          <p>That lesson doesn't exist.</p>
          <Link to="/training/catalog">Back to all modules</Link>
        </div>
      </div>
    );
  }

  const isLastLesson = n === mod.lessons.length;
  const isKnowledgeCheck = mod.id === 'm1' && isLastLesson;
  const body = MODULE1_LESSON_BODIES[lesson.id];

  function goToLesson(next: number) {
    navigate(`/training/module/${mod!.id}/lesson/${next}`);
  }

  function finishModule() {
    trackEvent('module_completed', { module_id: mod!.id });
    navigate(`/training/module/${mod!.id}`);
  }

  function selectAnswer(i: number) {
    if (answered !== null) return;
    setAnswered(i);
    if (!checkAttempted) {
      trackEvent('knowledge_check_attempted', { module_id: mod!.id });
      setCheckAttempted(true);
    }
    if (i === MODULE1_KNOWLEDGE_CHECK.correctIndex) {
      trackEvent('knowledge_check_passed', { module_id: mod!.id });
    }
  }

  return (
    <div className="tr-root">
      <div className="tr-wrap tr-appsec">
        <div className="tr-lz">
          <button className="tr-back" onClick={() => navigate(`/training/module/${mod.id}`)}>
            <i className="ti ti-arrow-left"></i> {mod.title}
          </button>
          <div className="tr-lz-nav">
            <span className="tr-lzpick">Lesson {n} of {mod.lessons.length}</span>
            <span className="tr-lz-time">{lesson.durationMin} min</span>
          </div>
          <div className="tr-bar">
            <i style={{ width: `${(n / mod.lessons.length) * 100}%` }} />
          </div>

          <h1>{lesson.title}</h1>

          {isKnowledgeCheck ? (
            <div className="tr-quiz">
              <div className="tr-k">Knowledge check · end of module</div>
              <h3>{MODULE1_KNOWLEDGE_CHECK.question}</h3>
              {MODULE1_KNOWLEDGE_CHECK.options.map((opt, i) => {
                const letter = String.fromCharCode(65 + i);
                const isCorrect = i === MODULE1_KNOWLEDGE_CHECK.correctIndex;
                const cls = answered === null ? '' : answered === i ? (isCorrect ? ' tr-correct' : ' tr-incorrect') : '';
                return (
                  <button key={opt} className={`tr-opt${cls}`} disabled={answered !== null} onClick={() => selectAnswer(i)}>
                    <i className="tr-letter">{letter}</i> {opt}
                  </button>
                );
              })}
              {answered !== null && (
                <p className={`tr-quiz-feedback ${answered === MODULE1_KNOWLEDGE_CHECK.correctIndex ? 'tr-correct' : 'tr-incorrect'}`}>
                  {answered === MODULE1_KNOWLEDGE_CHECK.correctIndex
                    ? 'Correct — that verifies the request through a channel the message itself didn\'t provide.'
                    : 'Not quite — the safest option verifies the request through a channel the message itself didn\'t provide.'}
                </p>
              )}
            </div>
          ) : body ? (
            <div className="tr-lz-body">
              {body.explanation.map((p) => <p key={p}>{p}</p>)}
              <div className="tr-callout">
                <div className="tr-k">{body.caseTitle}</div>
                {body.caseBody.map((p) => <p key={p}>{p}</p>)}
              </div>
              {body.afterCase?.map((p) => <p key={p}>{p}</p>)}
              <div className="tr-callout tr-take">
                <div className="tr-k">Take this with you</div>
                <p>{body.takeaway}</p>
              </div>
            </div>
          ) : (
            <div className="tr-lz-body">
              <p>
                Full lesson content for this module isn't part of this demo
                yet — Module 1 is the one built out end to end to show the
                shape a real lesson takes.
              </p>
            </div>
          )}

          <div className="tr-lz-foot">
            <button className="tr-btn tr-btn-ghost" disabled={n === 1} onClick={() => goToLesson(n - 1)}>
              <i className="ti ti-arrow-left"></i> Previous
            </button>
            {isLastLesson ? (
              <button
                className="tr-btn tr-btn-primary"
                disabled={isKnowledgeCheck && answered === null}
                onClick={() => {
                  trackEvent('lesson_completed', { module_id: mod.id, lesson_id: lesson.id });
                  finishModule();
                }}
              >
                Finish module
              </button>
            ) : (
              <button
                className="tr-btn tr-btn-primary"
                onClick={() => {
                  trackEvent('lesson_completed', { module_id: mod.id, lesson_id: lesson.id });
                  goToLesson(n + 1);
                }}
              >
                Next lesson <i className="ti ti-arrow-right"></i>
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
